import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const useCases = [
  {
    tab: 'Claims Intelligence',
    title: 'Claims Intelligence Suite',
    description:
      'Automate the most labor-intensive parts of your claims operation, document extraction, triage, fraud detection, while keeping your adjusters focused on complex decisions.',
    metrics: [
      { value: '30', unit: '–50%', label: 'Cycle time reduction' },
      { value: '85', unit: '%+', label: 'Document extraction accuracy' },
      { value: '3', unit: 'x', label: 'Adjuster throughput increase' },
      { value: '6', unit: '–8 wks', label: 'Time to live production' },
    ],
    pipelineLabel: 'Claims Pipeline',
    steps: [
      ['FNOL received, documents auto-ingested', '~0 sec'],
      ['Structured data extracted from PDFs/images', '~4 sec'],
      ['Triage model scores complexity + severity', '~2 sec'],
      ['Fraud pattern model flags anomalies', '~3 sec'],
      ['Auto-routed to correct adjuster queue', '~1 sec'],
      ['Adjuster receives pre-filled summary', '✓ Done'],
    ],
  },
  {
    tab: 'Underwriting',
    title: 'Underwriting Intelligence',
    description:
      'Equip your underwriters with intelligent risk scoring, submission prioritization, and third-party data enrichment, without replacing underwriter judgment or requiring new platforms.',
    metrics: [
      { value: '40', unit: '–60%', label: 'Manual underwriting time saved' },
      { value: '2', unit: 'x', label: 'Submission throughput increase' },
      { value: '12', unit: '–16 wks', label: 'Time to production' },
      { value: '10', unit: ':1', label: 'Typical first-year ROI' },
    ],
    pipelineLabel: 'Underwriting Pipeline',
    steps: [
      ['Submission received, auto data extraction', '~0 sec'],
      ['Third-party data enrichment (weather, property)', '~8 sec'],
      ['URST risk scoring model runs', '~3 sec'],
      ['Appetite matching against current guidelines', '~2 sec'],
      ['Priority score + recommendation generated', '~1 sec'],
      ['Underwriter reviews pre-analyzed submission', '✓ Done'],
    ],
  },
  {
    tab: 'Agent & Policy Assist',
    title: 'Agent & Policy Assist',
    description:
      'Give your agents and brokers instant access to accurate policy information, renewal predictions, and real-time coverage Q&A, reducing call center load and improving customer experience.',
    metrics: [
      { value: '60', unit: '%', label: 'Call handling time reduction' },
      { value: '95', unit: '%', label: 'Policy Q&A accuracy' },
      { value: '8', unit: '–12 wks', label: 'Time to live production' },
      { value: '4', unit: 'x', label: 'Agent query resolution speed' },
    ],
    pipelineLabel: 'Agent Assist Pipeline',
    steps: [
      ['Agent types natural language question', 'instant'],
      ['RAG retrieval from policy document corpus', '~1.5 sec'],
      ['LLM synthesizes accurate, cited answer', '~2 sec'],
      ['Renewal risk score surfaced for context', '~1 sec'],
      ['Suggested next action for agent', '~0 sec'],
      ['Interaction logged for compliance audit trail', '✓ Done'],
    ],
  },
  {
    tab: 'Regulatory Automation',
    title: 'Regulatory Reporting Automation',
    description:
      'Automate the most time-consuming compliance workflows, state DOI filings, loss run reports, and NAIC data submissions, reducing errors and freeing your compliance team for higher-value work.',
    metrics: [
      { value: '30', unit: '–50%', label: 'Compliance team hours saved' },
      { value: '99', unit: '%', label: 'Reporting accuracy rate' },
      { value: '8', unit: '–10 wks', label: 'Time to production' },
      { value: '100', unit: '%', label: 'Audit trail coverage' },
    ],
    pipelineLabel: 'Compliance Pipeline',
    steps: [
      ['Reporting period triggers automated pipeline', 'scheduled'],
      ['Data ingested from claims, policy, billing systems', 'auto'],
      ['Data quality and completeness validated', '~5 min'],
      ['Reports compiled per DOI/NAIC formats', '~10 min'],
      ['Human review of flagged exceptions only', '~30 min'],
      ['Submitted with full audit trail attached', '✓ Done'],
    ],
  },
];

const CaseStudies = () => {
  const [active, setActive] = useState(0);
  const current = useCases[active];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Use Cases — Solutions for Insurance Carriers | NIVA Digital AI"
        description="High-ROI solutions for insurance carriers: claims intelligence, underwriting, agent & policy assist, and regulatory automation, deployed in weeks."
        keywords="insurance use cases, claims intelligence, underwriting, agent assist, regulatory automation, NAIC compliance"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/case-studies`}
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50">
        <div className="container mx-auto max-w-6xl text-center">
          <p className="text-sm font-bold tracking-widest text-red-600 uppercase mb-3">Use Cases</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Solutions That Work for<br /><span className="text-red-600">Insurance Carriers</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We focus on the highest-ROI applications in insurance, the ones with clear, measurable outcomes that justify the investment within 12 months.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-12" data-testid="usecase-tabs">
            {useCases.map((uc, idx) => (
              <button
                key={uc.tab}
                onClick={() => setActive(idx)}
                data-testid={`usecase-tab-${idx}`}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  active === idx
                    ? 'bg-red-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {uc.tab}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="grid lg:grid-cols-2 gap-10 items-start" data-testid="usecase-content">
            {/* Left: text + metrics */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{current.title}</h3>
              <p className="text-gray-600 leading-relaxed mb-8">{current.description}</p>
              <div className="grid grid-cols-2 gap-4">
                {current.metrics.map((m) => (
                  <div key={m.label} className="border-2 border-red-100 rounded-xl p-5 bg-red-50/40">
                    <div className="text-3xl md:text-4xl font-bold text-gray-900">
                      {m.value}<span className="text-red-600">{m.unit}</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: pipeline visual */}
            <div className="bg-gray-900 rounded-2xl p-6 md:p-8">
              <div className="text-xs font-mono tracking-widest uppercase text-gray-500 mb-5">
                {current.pipelineLabel}
              </div>
              <div className="space-y-3">
                {current.steps.map(([text, result], i) => (
                  <div key={i} className="flex items-center gap-4 bg-gray-800/60 border border-gray-700/60 rounded-lg px-4 py-3">
                    <span className="w-7 h-7 rounded-full bg-red-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-200 flex-1">{text}</span>
                    <span className="text-xs font-mono text-green-400 flex-shrink-0">{result}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Operation?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Let&apos;s identify where we can deliver the highest ROI for your operation.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Book a Free Review <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
